import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import GuidelineModal from './GuidelineModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';
import { GuidelineType } from "../../../../types/GuidelineType";
const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));


const Guidelines: React.FC = () => {
  const [guidelines, setGuidelines] = useState<GuidelineType[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<GuidelineType | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchGuideline = async () => {
    try {
      const data = await apiRequest<GuidelineType[]>({
        method: 'get',
        url: 'guidelines',
      });
      setGuidelines(data);
    } catch (error) {
      showError('Failed to fetch guideline');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchGuideline();
  }, []);

  const handleEditClick = (record: GuidelineType) => {
    setIsEditMode(true);
    setInitialValues({
      guideline_id: record.guideline_id,
      guideline_title: record.guideline_title,
      guideline_description: record.guideline_description,
      guideline_short_description: record.guideline_short_description,
      guideline_slug: record.guideline_slug,
      guideline_video: record.guideline_video,
      guideline_link: record.guideline_link,
      guideline_status: record.guideline_status,
      guideline_image: record.guideline_image,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchGuideline();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (guideline_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-guideline/${guideline_id}`,
      });
      showSuccess('Guideline deleted');
      fetchGuideline();
    } catch {
      showError('Failed to delete guideline');
    }
  };

  const handleStatusToggle = async (guidelineId: number) => {
    setGuidelines((prev) =>
      prev.map((item) =>
        item.guideline_id === guidelineId
          ? { ...item, guideline_status: item.guideline_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-guideline/${guidelineId}`,
      });
      showSuccess('Guideline status updated');
    } catch (error) {
      showError('Failed to update status');
      setGuidelines((prev) =>
        prev.map((item) =>
          item.guideline_id === guidelineId
            ? { ...item, guideline_status: item.guideline_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<GuidelineType>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<GuidelineType> = [

    {
      title: 'Title',
      dataIndex: 'guideline_title',
      key: 'guideline_title',
      filters: Array.from(new Set(guidelines.map((s) => s.guideline_title))).map((name) => ({
        text: name,
        value: name,
      })),
      width: 200,
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.guideline_title || null,
      onFilter: (value, record) => record.guideline_title.includes(value as string),
      sorter: (a, b) => a.guideline_title.localeCompare(b.guideline_title),
      sortOrder: sortedInfo.columnKey === 'guideline_title' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div className='text-wrap'>{record.guideline_title}</div>,
    },
    {
      title: 'Link',
      dataIndex: 'guideline_link',
      key: 'guideline_link',
      width: 150,
      filters: Array.from(new Set(guidelines.map((s) => s.guideline_link))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.guideline_link || null,
      onFilter: (value, record) => record.guideline_link.includes(value as string),
      sorter: (a, b) => a.guideline_link.localeCompare(b.guideline_link),
      sortOrder: sortedInfo.columnKey === 'guideline_link' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div className='text-wrap'>{record.guideline_link}</div>,
    },
    {
      title: 'Short Description',
      dataIndex: 'guideline_short_description',
      key: 'guideline_short_description',
      width: 300,
      filters: Array.from(new Set(guidelines.map((s) => s.guideline_short_description))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.guideline_short_description || null,
      onFilter: (value, record) => record.guideline_short_description.includes(value as string),
      sorter: (a, b) => a.guideline_short_description.localeCompare(b.guideline_short_description),
      sortOrder: sortedInfo.columnKey === 'guideline_short_description' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div className='text-wrap'>{record.guideline_short_description}</div>,
    },
    {
      title: 'Description',
      dataIndex: 'guideline_description',
      key: 'guideline_description',
      width: 300,
      filters: Array.from(new Set(guidelines.map((s) => s.guideline_description))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.guideline_description || null,
      onFilter: (value, record) => record.guideline_description.includes(value as string),
      sorter: (a, b) => a.guideline_description.localeCompare(b.guideline_description),
      sortOrder: sortedInfo.columnKey === 'guideline_description' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div className='text-wrap'>{record.guideline_description}</div>,
    },
    {
      title: 'Image',
      dataIndex: 'guideline_image',
      key: 'guideline_image',
      width: 150,
      render: (_: any, record: any) =>
        record ? (
          <img
            src={record.guideline_image}
            alt="guideline"
            style={{
              width: 40,
              height: 50,
              objectFit: 'cover',
              borderRadius: 8,
            }}
          />
        ) : (
          '-'
        ),
    },
    {
      title: 'Video',
      dataIndex: 'guideline_video',
      key: 'guideline_video',
      width: 150,
      filters: Array.from(new Set(guidelines.map((s) => s.guideline_video))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.guideline_video || null,
      onFilter: (value, record) => record.guideline_video.includes(value as string),
      sorter: (a, b) => a.guideline_video.localeCompare(b.guideline_video),
      sortOrder: sortedInfo.columnKey === 'guideline_video' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div className='text-wrap'>{record.guideline_video}</div>,
    },
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      fixed: 'right',
      render: (_: any, record: GuidelineType) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.guideline_status}
                onChange={() => handleStatusToggle(record.guideline_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this guideline?"
              onConfirm={() => handleDelete(record.guideline_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Guideline</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Guideline
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<GuidelineType>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="guideline_id"
          dataSource={guidelines.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          // scroll={{ y: 37 * 13 }}
           scroll={{ x: 1800, y: 37 * 13 }}
          locale={{ emptyText: 'No guideline found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, guidelines.length)} of{' '}
                {guidelines.length}
              </span>
              <Pagination
                {...pagination}
                total={guidelines.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <GuidelineModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
        guidelines={guidelines}
      />
    </>
  );
};

export default Guidelines;